/**
 * RedirectIfAuthed — wraps the /login route (BUILD_SPEC.md §10.1).
 *
 * If the user is already authenticated, sends them straight to
 * /dashboard. Avoids the awkward case where a logged-in user types
 * /login into the URL bar and gets a redundant form.
 *
 * Honors `location.state.from` the same way LoginPage does, so an
 * already-authed user who hits /login via a stale tab (or a deep
 * link that was bounced before the auth store hydrated) still lands
 * on their intended destination. The per-route guards (AdminLayout)
 * catch any role mismatch downstream — no need to validate here.
 */

import { type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { roleHome } from "@/lib/roleHome";

interface Props {
  children: ReactNode;
}

export function RedirectIfAuthed({ children }: Props) {
  const isAuthed = useAuthStore((s) => !!s.accessToken && !!s.user);
  const role = useAuthStore((s) => s.user?.role);
  const location = useLocation();
  if (isAuthed) {
    // DISPLAY (TV kiosk) is wall-only: always send it home to /wall,
    // ignoring any stale from-path. Other roles honor the deep-link
    // round-trip pattern from RequireAuth (or go home on explicit logout).
    if (role === "DISPLAY") return <Navigate to={roleHome(role)} replace />;
    const lstate = location.state as { from?: { pathname?: string }; logout?: boolean } | null;
    const redirectTo = lstate?.logout ? "/dashboard" : (lstate?.from?.pathname ?? "/dashboard");
    return <Navigate to={redirectTo} replace />;
  }
  return <>{children}</>;
}
